import React, { PropsWithChildren, useCallback, useRef, useState } from "react";

import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import { IconButton, InputAdornment } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

import searchIcon from "@src/assets/svg/search_icon.svg";
import ClientsTable from "@src/multiCustodian/components/Advisor/Clients/ClientsTable";
import { ClientSearchFiltersCheckboxLabel } from "@src/multiCustodian/components/Advisor/Clients/types";
import ButtonPrimary from "@src/multiCustodian/components/MUI/Button/Button";
import Popover from "@src/sharedComponents/Popover/Popover";
import ScrollableCheckboxList, {
  CheckboxListConfig,
} from "@src/sharedComponents/ScrollableCheckboxList/ScrollableCheckboxList";
import useResetTablePaginationPage from "@src/sharedComponents/Table/useResetTablePaginationPage";
import TextInput from "@src/sharedComponents/TextInput/TextInput";

import AdvisorContainer from "./AdvisorContainer";
import AdvisorContentContainer from "./AdvisorContentContainer";
import { ClientsFiltersProvider, useClientsFilters } from "./Clients/context";
import Households from "./Households/Households";

import * as styles from "./Clients.module.css";

const popoverDropdownMenuWidth = 136;

// Separating the UI component to use context
const ClientsContent: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const filterPopoverAnchorRef = useRef<HTMLDivElement | null>(null);
  const [showFilterPopover, setShowFilterPopover] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();
  const resetTablePaginationPage = useResetTablePaginationPage();

  const { search, searchFilters, setSearch, setSearchFilters } =
    useClientsFilters();

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
      resetTablePaginationPage();
    },
    [resetTablePaginationPage, setSearch]
  );

  const onChangeClientSearchFilters = (
    config: CheckboxListConfig<ClientSearchFiltersCheckboxLabel>
  ) => {
    if (Object.values(config).filter((val) => val.checked).length === 0) {
      // always have 1 filter checked
      return;
    }

    setSearchFilters(config);
  };

  const openCreateClient = useCallback(() => {
    navigate({ ...location, pathname: "/Advisor/Clients/Create" });
  }, [navigate, location]);

  return (
    <AdvisorContentContainer heroTitle={"Clients"}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div ref={filterPopoverAnchorRef}>
            <TextInput
              label=""
              value={search}
              onChange={handleOnChange}
              placeholder="Search clients..."
              style={{ width: "260px" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img src={searchIcon} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {search && (
                      <IconButton
                        aria-label="clear search"
                        onClick={() => {
                          setSearch("");
                          resetTablePaginationPage();
                        }}
                        edge="end"
                        size="small"
                      >
                        <ClearIcon
                          fontSize="small"
                          className={styles.clearIcon}
                        />
                      </IconButton>
                    )}
                    <IconButton
                      aria-label="filter search"
                      onClick={() => setShowFilterPopover(!showFilterPopover)}
                      edge="end"
                      size="small"
                    >
                      <FilterListIcon className={styles.filterIcon} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <Popover
            open={showFilterPopover}
            onClose={() => setShowFilterPopover(false)}
            anchor={filterPopoverAnchorRef.current}
            transformOriginVertical={-4}
            anchorOriginHorizontal="right"
            transformOriginHorizontal="right"
            popoverElement={null}
          >
            <ScrollableCheckboxList
              haveSelectAll={false}
              checkboxesConfig={searchFilters}
              setCheckboxesConfig={onChangeClientSearchFilters}
              multiSelectOff={false}
              minWidth={popoverDropdownMenuWidth}
            />
          </Popover>
        </div>

        <ButtonPrimary
          text="Create Client"
          onClick={openCreateClient}
          variant="outlined"
          buttonType="primary"
          sx={{ height: "32px" }}
        />
      </div>

      <ClientsTable search={search} searchFilters={searchFilters} />

      <div>{children}</div>
    </AdvisorContentContainer>
  );
};

// Main component that provides the context
const Clients: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { enableHouseholds } = useFlags();

  if (enableHouseholds) {
    // TODO: Migrate all client components to Households when ready
    return <Households>{children}</Households>;
  }

  return (
    <AdvisorContainer>
      <ClientsFiltersProvider>
        <ClientsContent>{children}</ClientsContent>
      </ClientsFiltersProvider>
    </AdvisorContainer>
  );
};

export default Clients;
